import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type {
  DistiListingConfigUpdate,
  UpdateVendorRequest,
  Vendor,
} from '@techgitter/contracts';
import { DistiListingConfigPanel } from './DistiListingConfigPanel';
import { updateDistiListingConfig } from '../../services/distiListingsApi';

// Mirrors the cr001_supplier_match_key optionset on cr001_vendors — how a
// subscription supplier's import file identifies the customer. Codes must
// match the Dataverse option values exactly.
const SUPPLIER_MATCH_KEY_OPTIONS: { value: number; label: string }[] = [
  { value: 468190000, label: 'External Id' },
  { value: 468190001, label: 'Customer Name' },
  { value: 468190002, label: 'Company Id' },
];

interface VendorEditDialogProps {
  open: boolean;
  vendor: Vendor | null;
  /**
   * Persists the basic vendor fields. Returns the updated vendor so the
   * dialog can refresh its local copy without a re-fetch.
   */
  onSave: (vendorId: string, payload: UpdateVendorRequest) => Promise<Vendor>;
  onClose: () => void;
  /**
   * Fired after **any** successful save step (basic-fields, listing-config,
   * or both). The parent should patch its vendors-list cache with the
   * supplied row so the next open of this dialog reflects the latest
   * server state. Without this hook listing-only saves leak as a
   * stale-cache bug — the basic onSave path updates the list, the
   * listings PATCH does not.
   */
  onVendorUpdated?: (vendor: Vendor) => void;
  /**
   * Optional initial error from the parent (e.g. failed open). Save errors
   * are managed locally below.
   */
  error?: string | null;
  /** Kept for back-compat with the parent's loading state if it still passes one. */
  isSaving?: boolean;
}

export const VendorEditDialog: React.FC<VendorEditDialogProps> = ({
  open,
  vendor,
  isSaving: parentSaving,
  error: parentError,
  onClose,
  onSave,
  onVendorUpdated,
}) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDisti, setIsDisti] = useState(false);
  const [isThirdPartyService, setIsThirdPartyService] = useState(false);
  const [isRetailer, setIsRetailer] = useState(false);
  const [isSubscriptionSupplier, setIsSubscriptionSupplier] = useState(false);
  // Subscription-supplier-only: optionset code, or null when unset.
  const [supplierMatchKey, setSupplierMatchKey] = useState<number | null>(null);
  // Disti-only fields. Stored as strings for clean controlled-input UX
  // (an empty string round-trips to null on save, so admins can clear
  // either field without typing 0).
  const [shippingDefault, setShippingDefault] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [shippingNotes, setShippingNotes] = useState('');
  const [urlScrapeEnabled, setUrlScrapeEnabled] = useState(false);
  // Per-disti transaction-fee rate, edited as a percentage (e.g. "1.50"
  // for 1.5%). Converted to a fraction on save. Empty string round-trips
  // to 0 — i.e. "no fee charged by this vendor".
  const [transactionFeeRatePct, setTransactionFeeRatePct] = useState('');
  // Local copy of the vendor — updated after each save so child panels
  // see the freshest config without re-mounting.
  const [liveVendor, setLiveVendor] = useState<Vendor | null>(null);
  // Listing draft surfaced from the child panel via onChange.
  const [listingDraft, setListingDraft] = useState<DistiListingConfigUpdate | null>(null);
  const [listingDirty, setListingDirty] = useState(false);
  const [listingValid, setListingValid] = useState(true);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!vendor) return;
    setCode(vendor.code);
    setName(vendor.name);
    setIsActive(vendor.isActive);
    setIsDisti(Boolean(vendor.isDisti));
    setIsThirdPartyService(Boolean(vendor.isThirdPartyService));
    setIsRetailer(Boolean(vendor.isRetailer));
    setIsSubscriptionSupplier(Boolean(vendor.isSubscriptionSupplier));
    setSupplierMatchKey(vendor.supplierMatchKey ?? null);
    setShippingDefault(
      vendor.shippingDefaultExGst != null ? String(vendor.shippingDefaultExGst) : '',
    );
    setDeliveryDays(
      vendor.deliveryDays != null ? String(vendor.deliveryDays) : '',
    );
    setShippingNotes(vendor.shippingNotes ?? '');
    setUrlScrapeEnabled(Boolean(vendor.urlScrapeEnabled));
    setTransactionFeeRatePct(
      vendor.transactionFeeRate
        ? (vendor.transactionFeeRate * 100).toFixed(2).replace(/\.?0+$/, '')
        : '',
    );
    setLiveVendor(vendor);
    // Reset listing draft tracking — the child panel re-emits on its own
    // useEffect once it remounts against the new vendor.
    setListingDraft(null);
    setListingDirty(false);
    setListingValid(true);
    setSaveError(null);
  }, [vendor]);

  const handlePanelChange = useCallback(
    (
      draft: DistiListingConfigUpdate | null,
      state: { dirty: boolean; valid: boolean },
    ) => {
      setListingDraft(draft);
      setListingDirty(state.dirty);
      setListingValid(state.valid);
    },
    [],
  );

  if (!vendor) {
    return null;
  }

  const trimmedCode = code.trim();
  const trimmedName = name.trim();
  const trimmedShipping = shippingDefault.trim();
  const trimmedDelivery = deliveryDays.trim();
  const trimmedFeeRatePct = transactionFeeRatePct.trim();
  const parsedShipping: number | null =
    trimmedShipping === '' ? null : Number(trimmedShipping);
  const parsedDelivery: number | null =
    trimmedDelivery === '' ? null : Number(trimmedDelivery);
  // Empty input = 0 (i.e. this vendor charges no transaction fee).
  // Otherwise validate as a percentage in [0, 100].
  const parsedFeeRatePctNumber: number =
    trimmedFeeRatePct === '' ? 0 : Number(trimmedFeeRatePct);
  const parsedFeeRateFraction: number =
    Number.isFinite(parsedFeeRatePctNumber) ? parsedFeeRatePctNumber / 100 : 0;
  const shippingValid =
    trimmedShipping === '' ||
    (Number.isFinite(parsedShipping) && (parsedShipping ?? 0) >= 0);
  const deliveryValid =
    trimmedDelivery === '' ||
    (Number.isFinite(parsedDelivery) &&
      Number.isInteger(parsedDelivery) &&
      (parsedDelivery ?? 0) >= 0);
  const feeRateValid =
    trimmedFeeRatePct === '' ||
    (Number.isFinite(parsedFeeRatePctNumber) &&
      parsedFeeRatePctNumber >= 0 &&
      parsedFeeRatePctNumber <= 100);

  const currentShipping = vendor.shippingDefaultExGst ?? null;
  const currentDelivery = vendor.deliveryDays ?? null;
  const currentNotes = vendor.shippingNotes ?? '';
  const currentUrlScrape = Boolean(vendor.urlScrapeEnabled);
  const currentFeeRate = vendor.transactionFeeRate ?? 0;
  const shippingDirty = parsedShipping !== currentShipping;
  const deliveryDirty = parsedDelivery !== currentDelivery;
  const notesDirty = shippingNotes !== currentNotes;
  const urlScrapeDirty = urlScrapeEnabled !== currentUrlScrape;
  // Tiny epsilon to absorb rounding noise from the percentage-string
  // round-trip (e.g. 0.0111 → "1.11" → 0.0111 should not count as dirty).
  const feeRateDirty = Math.abs(parsedFeeRateFraction - currentFeeRate) > 1e-9;

  const basicDirty =
    trimmedCode !== vendor.code ||
    trimmedName !== vendor.name ||
    isActive !== vendor.isActive ||
    isDisti !== Boolean(vendor.isDisti) ||
    isThirdPartyService !== Boolean(vendor.isThirdPartyService) ||
    isRetailer !== Boolean(vendor.isRetailer) ||
    isSubscriptionSupplier !== Boolean(vendor.isSubscriptionSupplier) ||
    supplierMatchKey !== (vendor.supplierMatchKey ?? null) ||
    shippingDirty ||
    deliveryDirty ||
    notesDirty ||
    urlScrapeDirty ||
    feeRateDirty;

  // Listing dirtiness only matters when the vendor IS a disti — the
  // listings panel is hidden otherwise, and we shouldn't ask the backend
  // to patch listing fields on a non-disti.
  const listingDirtyEffective = isDisti && listingDirty;

  const isDirty = basicDirty || listingDirtyEffective;
  const isValid =
    trimmedCode.length > 0 &&
    trimmedName.length > 0 &&
    shippingValid &&
    deliveryValid &&
    feeRateValid &&
    (!listingDirtyEffective || listingValid);

  const handleSave = async () => {
    if (!isDirty || !isValid || saving) return;
    setSaveError(null);
    setSaving(true);
    try {
      let next = liveVendor ?? vendor;

      if (basicDirty) {
        next = await onSave(vendor.vendorId, {
          code: trimmedCode,
          name: trimmedName,
          isActive,
          isDisti,
          isThirdPartyService,
          isRetailer,
          isSubscriptionSupplier,
          supplierMatchKey,
          shippingDefaultExGst: parsedShipping,
          deliveryDays: parsedDelivery,
          shippingNotes: shippingNotes.trim() === '' ? null : shippingNotes,
          urlScrapeEnabled,
          transactionFeeRate: parsedFeeRateFraction,
        });
      }

      if (listingDirtyEffective && listingDraft) {
        // The /listings/config endpoint returns the full vendor row with
        // the freshly-saved basic fields too — its return value is the
        // canonical post-save state.
        next = await updateDistiListingConfig(vendor.vendorId, listingDraft);
      }

      setLiveVendor(next);
      setCode(next.code);
      setName(next.name);
      setIsActive(next.isActive);
      setIsDisti(Boolean(next.isDisti));
      setIsThirdPartyService(Boolean(next.isThirdPartyService));
      setIsRetailer(Boolean(next.isRetailer));
      setIsSubscriptionSupplier(Boolean(next.isSubscriptionSupplier));
      setSupplierMatchKey(next.supplierMatchKey ?? null);
      setShippingDefault(
        next.shippingDefaultExGst != null ? String(next.shippingDefaultExGst) : '',
      );
      setDeliveryDays(
        next.deliveryDays != null ? String(next.deliveryDays) : '',
      );
      setShippingNotes(next.shippingNotes ?? '');
      setUrlScrapeEnabled(Boolean(next.urlScrapeEnabled));
      setTransactionFeeRatePct(
        next.transactionFeeRate
          ? (next.transactionFeeRate * 100).toFixed(2).replace(/\.?0+$/, '')
          : '',
      );
      // Push the final server state back to the parent so its vendors
      // list reflects the listing-config changes too. Without this, a
      // listing-only save persists server-side but the parent keeps
      // serving the stale row on next dialog open.
      onVendorUpdated?.(next);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const dialogBusy = saving || Boolean(parentSaving);

  return (
    <Dialog open={open} onClose={dialogBusy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Vendor</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {parentError && <Alert severity="error">{parentError}</Alert>}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
              sx={{ flex: { sm: '0 0 200px' } }}
            />
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              sx={{ flex: 1, maxWidth: 320 }}
            />
          </Stack>

          <Divider textAlign="left">
            <Typography variant="overline" color="text.secondary">
              Classification
            </Typography>
          </Divider>
          <Box
            sx={{
              display: 'grid',
              // First column matches the Code field's fixed width (200px) so
              // the second toggle column lines up with the Name field above.
              gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
              columnGap: 2,
              rowGap: 0.5,
            }}
          >
            <FormControlLabel
              control={(
                <Switch
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
              )}
              label="Active"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={isThirdPartyService}
                  onChange={(event) =>
                    setIsThirdPartyService(event.target.checked)
                  }
                />
              )}
              label="Third-party service provider"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={isDisti}
                  onChange={(event) => setIsDisti(event.target.checked)}
                />
              )}
              label="Distributor"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={isRetailer}
                  onChange={(event) => setIsRetailer(event.target.checked)}
                />
              )}
              label="Retailer (prices inc-GST)"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={isSubscriptionSupplier}
                  onChange={(event) =>
                    setIsSubscriptionSupplier(event.target.checked)
                  }
                />
              )}
              label="Subscription supplier"
            />
          </Box>
          {isSubscriptionSupplier && (
            <TextField
              select
              label="Supplier match key"
              value={supplierMatchKey ?? ''}
              onChange={(event) =>
                setSupplierMatchKey(
                  event.target.value === '' ? null : Number(event.target.value),
                )
              }
              size="small"
              helperText="How this supplier's import file identifies the customer. Drives subscription reconciliation matching."
              sx={{ width: 200 }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {SUPPLIER_MATCH_KEY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Divider textAlign="left">
            <Typography variant="overline" color="text.secondary">
              Fees
            </Typography>
          </Divider>
          <TextField
            label="EFT fee %"
            value={transactionFeeRatePct}
            onChange={(event) => setTransactionFeeRatePct(event.target.value)}
            size="small"
            inputMode="decimal"
            placeholder="0"
            error={!feeRateValid}
            helperText={
              feeRateValid
                ? 'Payment-processing surcharge this vendor charges us. 0–100 (blank = none).'
                : 'Must be 0–100'
            }
            sx={{ width: 200 }}
          />
          {isDisti && (
            <Stack spacing={2}>
              <Divider textAlign="left">
                <Typography variant="overline" color="text.secondary">
                  Distributor settings
                </Typography>
              </Divider>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Default shipping (ex-GST)"
                  value={shippingDefault}
                  onChange={(event) => setShippingDefault(event.target.value)}
                  size="small"
                  inputMode="decimal"
                  placeholder="0.00"
                  error={!shippingValid}
                  helperText={
                    shippingValid
                      ? 'Used in the picker pricing breakdown. Internal-only — never shown to customers.'
                      : 'Must be a non-negative number'
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Delivery (days)"
                  value={deliveryDays}
                  onChange={(event) => setDeliveryDays(event.target.value)}
                  size="small"
                  inputMode="numeric"
                  placeholder="3"
                  error={!deliveryValid}
                  helperText={
                    deliveryValid ? 'Indicative lead time' : 'Whole number ≥ 0'
                  }
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                label="Shipping notes"
                value={shippingNotes}
                onChange={(event) => setShippingNotes(event.target.value)}
                size="small"
                multiline
                minRows={2}
                placeholder="e.g. Order needs to be placed by 2pm for next-day delivery"
                helperText="Surfaced inline in the picker so the tech sees the constraint before quoting. Internal-only."
                fullWidth
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={urlScrapeEnabled}
                    onChange={(event) => setUrlScrapeEnabled(event.target.checked)}
                  />
                )}
                label="Allow URL spec scrape"
              />
            </Stack>
          )}
          {liveVendor && isDisti && (
            <>
              <Divider textAlign="left">
                <Typography variant="overline" color="text.secondary">
                  Listing feed
                </Typography>
              </Divider>
              <Box>
                <DistiListingConfigPanel
                  vendor={liveVendor}
                  onChange={handlePanelChange}
                />
              </Box>
            </>
          )}
          {liveVendor && !isDisti && vendor.isDisti && (
            <Alert severity="info">
              This vendor was previously flagged as a distributor — saving
              will hide its listings config. Existing listings remain in the
              database; toggle distributor back on to manage them.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={dialogBusy}>
          {isDirty ? 'Cancel' : 'Close'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isDirty || !isValid || dialogBusy}
        >
          {dialogBusy ? <CircularProgress size={16} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
